import type { FieldDto } from "../../types/field";
import type { FormDto } from "../../types/form";
import type {
  FormBuilderDraft,
  FormBuilderField,
  FormBuilderSection,
  FormSavePayload,
} from "../../types/formBuilder";
import type { SectionDto } from "../../types/section";
import type {
  VisibilityAction,
  VisibilityConfig,
  VisibilityOperator,
} from "../../types/visibility";
import {
  isConsentFieldType,
  isDisplayFieldType,
  isKnownFieldType,
  toApiFieldType,
  type ApiFieldTypeOption,
} from "./apiFieldTypes";
import {
  isFieldOptionsObject,
  resolveChoicesByConfig,
  resolveChoicesUrlConfig,
} from "../forms/fieldOptions";
import {
  inferListChoicesMode,
  isListFieldType,
  listChoicesMode,
} from "./listChoicesMode";
import {
  isIsoDateTimeString,
  isValidDateSubmit,
  normalizeDateTimeSubmit,
  parseCalendarDate,
  readOptionalAge,
  toIsoDate,
} from "../forms/dateField";
import { pad2, parseIsoDateTime } from "../dates/isoDate";
import {
  getFormClosedMessage,
  isFormClosed,
  isShowSubmissionAfterSubmit,
  resolveInteractionMode,
} from "../forms/formMeta";
import {
  DEFAULT_QUIZ_CONFIG,
  parseQuizCorrect,
  parseQuizPoints,
  resolveQuizConfig,
  serializeQuizConfig,
  serializeQuizCorrect,
} from "../forms/quiz";
import { defaultPrivacyFieldLabel } from "../forms/privacyConsent";
import { resolveFieldType } from "../forms/normalizeFieldType";
import { normalizeSubmissionFieldId } from "../forms/normalizeSubmission";
import { resolveDisplayConfig } from "../forms/displayField";
import {
  appearanceOverridesFromDto,
  buildAppearanceSavePayload,
  FALLBACK_FORM_THEMES,
} from "../forms/appearance";
import {
  readFieldDescription,
  serializeFieldDescription,
} from "../forms/fieldDescription";
import { readFieldLabel, serializeFieldLabel } from "../forms/fieldLabel";
import {
  resolveNumberConfig,
  serializeNumberOptions,
} from "../forms/numberField";
import { parseBoolean } from "../forms/parseBoolean";
import {
  normalizeSubmitHookForEditor,
  normalizeSubmitHooksForSave,
  resolveSubmitHooksFromForm,
  submitHooksTextFromForm,
} from "./submitHooks";
import {
  isDateLikeFieldType,
  visibilityOperatorNeedsValue,
} from "./visibilityOperators";

import { parseChoiceLines } from "./choiceLines";
import { uniqueFieldName } from "./fieldName";

export {
  mergeSubmitHooksTextAfterSave,
  submitHooksTextFromForm,
} from "./submitHooks";

let clientIdSeq = 0;

export function nextClientId(prefix: string): string {
  clientIdSeq += 1;
  return `${prefix}-${clientIdSeq}`;
}

export function generateFieldName(preferred = "field"): string {
  return uniqueFieldName([], preferred);
}

export function generateUniqueFieldName(
  existingNames: Iterable<string>,
  preferred = "field",
): string {
  return uniqueFieldName(existingNames, preferred);
}

export function createEmptyField(
  order = 0,
  existingNames?: Iterable<string>,
): FormBuilderField {
  return {
    clientId: nextClientId("field"),
    label: "",
    description: "",
    name: existingNames
      ? generateUniqueFieldName(existingNames, "text")
      : generateFieldName("text"),
    type: "text",
    required: false,
    order,
    choicesText: "",
    quizCorrect: [],
    quizPoints: 1,
    filterable: false,
    withTime: false,
    numberMin: null,
    numberMax: null,
    numberStep: null,
    numberInteger: false,
    minAge: null,
    maxAge: null,
    cityIds: [],
    cityLabels: {},
    choicesByField: "",
    choicesByMapText: {},
    choicesListMode: "manual",
    choicesUrl: "",
    choicesPath: "",
    choicesItem: "",
    choicesValue: "",
    choicesByUrl: "",
    choicesByPath: "",
    choicesByItem: "",
    choicesByValue: "",
    patronymicRequired: false,
    presentation: "stacked",
    visibilityEnabled: false,
    visibilityField: "",
    visibilityOperator: "equals",
    visibilityValue: "",
    visibilityAction: "show",
    nameManuallyEdited: false,
    displayContent: "",
    displayImageUrl: "",
    displayImageNodeId: null,
    yandexType: null,
  };
}

export function createEmptySection(order = 0): FormBuilderSection {
  return {
    clientId: nextClientId("section"),
    label: order === 0 ? "Основные данные" : `Раздел ${order + 1}`,
    order,
    fields: [],
  };
}

export function createEmptyDraft(): FormBuilderDraft {
  return {
    name: "",
    description: "",
    isClosed: false,
    closedMessage: "",
    submitHooksText: "[]",
    sections: [createEmptySection(0)],
    showSectionLabels: true,
    sectionDisplayMode: "all",
    showSubmissionAfterSubmit: false,
    interactionMode: "form",
    quiz: { ...DEFAULT_QUIZ_CONFIG },
    appearanceTheme: FALLBACK_FORM_THEMES.defaultTheme,
    appearanceOverrides: {},
    appearanceCleared: false,
  };
}

export function copyFormName(name: string): string {
  const base = name.trim() || "Форма";
  const copyPattern = /^(.+?) \((копия(?: \d+)?)\)$/;
  const match = base.match(copyPattern);

  if (match) {
    const root = match[1];
    const numMatch = match[2].match(/\d+/);
    const next = numMatch ? Number(numMatch[0]) + 1 : 2;
    return `${root} (копия ${next})`;
  }

  return `${base} (копия)`;
}

function cloneFieldForCopy(field: FormBuilderField): FormBuilderField {
  const { id: _id, ...rest } = field;
  return {
    ...rest,
    clientId: nextClientId("field"),
  };
}

function cloneSectionForCopy(section: FormBuilderSection): FormBuilderSection {
  const { id: _id, ...rest } = section;
  return {
    ...rest,
    clientId: nextClientId("section"),
    fields: section.fields.map(cloneFieldForCopy),
  };
}

/** Копия формы как новый черновик: без id разделов/полей, не опубликована. */
export function copyDraftForNewForm(
  source: FormBuilderDraft,
): FormBuilderDraft {
  return {
    name: copyFormName(source.name),
    description: source.description,
    isClosed: true,
    closedMessage: "",
    submitHooksText: source.submitHooksText,
    sections: source.sections.map(cloneSectionForCopy),
    showSectionLabels: source.showSectionLabels,
    sectionDisplayMode: source.sectionDisplayMode,
    showSubmissionAfterSubmit: source.showSubmissionAfterSubmit,
    interactionMode: source.interactionMode,
    quiz: { ...source.quiz },
    appearanceTheme: source.appearanceTheme,
    appearanceOverrides: { ...source.appearanceOverrides },
    appearanceCleared: source.appearanceCleared,
  };
}

function normalizeSubmitHookItem(item: unknown): unknown {
  return normalizeSubmitHookForEditor(item);
}

function normalizeSubmitHooks(form: FormDto): unknown[] {
  return resolveSubmitHooksFromForm(form).map(normalizeSubmitHookItem);
}

function choicesFromField(field: FieldDto): string {
  if (resolveFieldType(field.type) === "city") {
    return "";
  }
  if (resolveChoicesByConfig(field)) {
    return "";
  }

  const options = field.options;
  const rawChoices = Array.isArray(options)
    ? options
    : isFieldOptionsObject(options) && Array.isArray(options.choices)
      ? options.choices
      : [];

  if (!Array.isArray(rawChoices) || rawChoices.length === 0) {
    return "";
  }

  return rawChoices
    .map((item) => {
      if (typeof item === "string") return item;
      if (item && typeof item === "object") {
        const record = item as { value?: unknown; label?: unknown };
        return String(record.label ?? record.value ?? "").trim();
      }
      return "";
    })
    .filter(Boolean)
    .join("\n");
}

function cityIdsFromField(field: FieldDto): number[] {
  const fromCity = field.city?.city_ids;
  if (Array.isArray(fromCity) && fromCity.length > 0) {
    return fromCity
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id))
      .map((id) => Math.trunc(id));
  }

  if (
    isFieldOptionsObject(field.options) &&
    Array.isArray(field.options.city_ids)
  ) {
    const fromOptions = field.options.city_ids
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id))
      .map((id) => Math.trunc(id));
    if (fromOptions.length > 0) return fromOptions;
  }

  const choices =
    field.city?.choices ??
    (isFieldOptionsObject(field.options) && Array.isArray(field.options.choices)
      ? field.options.choices
      : null);
  if (!Array.isArray(choices)) return [];

  const fromChoices: number[] = [];
  for (const item of choices) {
    if (typeof item === "string") continue;
    if (
      item &&
      typeof item === "object" &&
      "value" in item &&
      item.value != null
    ) {
      const id = Number(item.value);
      if (Number.isFinite(id)) fromChoices.push(Math.trunc(id));
    }
  }
  return fromChoices;
}

function cityLabelsFromField(field: FieldDto): Record<string, string> {
  const labels: Record<string, string> = {};
  const choices =
    field.city?.choices ??
    (isFieldOptionsObject(field.options) && Array.isArray(field.options.choices)
      ? field.options.choices
      : null);
  if (!Array.isArray(choices)) return labels;

  for (const item of choices) {
    if (typeof item === "string") continue;
    if (
      item &&
      typeof item === "object" &&
      "value" in item &&
      item.value != null
    ) {
      const key = String(item.value);
      const label = String(
        "label" in item ? (item.label ?? item.value) : item.value,
      ).trim();
      if (key && label) labels[key] = label;
    }
  }
  return labels;
}

function choicesByFromField(
  field: FieldDto,
): Pick<
  FormBuilderField,
  | "choicesByField"
  | "choicesByMapText"
  | "choicesByUrl"
  | "choicesByPath"
  | "choicesByItem"
  | "choicesByValue"
> {
  const config = resolveChoicesByConfig(field);
  if (!config) {
    return {
      choicesByField: "",
      choicesByMapText: {},
      choicesByUrl: "",
      choicesByPath: "",
      choicesByItem: "",
      choicesByValue: "",
    };
  }

  const choicesByMapText: Record<string, string> = {};
  for (const [key, values] of Object.entries(config.map)) {
    choicesByMapText[key] = values.join("\n");
  }

  return {
    choicesByField: config.field,
    choicesByMapText,
    choicesByUrl: config.url ?? "",
    choicesByPath: config.path ?? "",
    choicesByItem: config.item ?? "",
    choicesByValue: config.value ?? "",
  };
}

function choicesUrlFromField(
  field: FieldDto,
): Pick<
  FormBuilderField,
  "choicesUrl" | "choicesPath" | "choicesItem" | "choicesValue"
> {
  const config = resolveChoicesUrlConfig(field);
  if (!config || resolveChoicesByConfig(field)) {
    return {
      choicesUrl: "",
      choicesPath: "",
      choicesItem: "",
      choicesValue: "",
    };
  }
  return {
    choicesUrl: config.url,
    choicesPath: config.path,
    choicesItem: config.item,
    choicesValue: config.value,
  };
}

function effectiveRemoteUrl(url: string): string {
  const trimmed = url.trim();
  if (!trimmed || trimmed === "https://") return "";
  return trimmed;
}

function buildChoicesByMap(
  mapText: Record<string, string>,
): Record<string, string[]> {
  const map: Record<string, string[]> = {};
  for (const [key, text] of Object.entries(mapText)) {
    const choices = parseChoiceLines(text);
    if (choices.length > 0) {
      map[String(key)] = choices;
    }
  }
  return map;
}

function visibilityValueFromRule(rule: {
  operator?: string;
  value?: unknown;
}): string {
  const operator = String(rule.operator ?? "equals");
  if (operator === "in") {
    const list = Array.isArray(rule.value) ? rule.value : [rule.value];
    return list
      .map((item) => String(item ?? "").trim())
      .filter((item) => item !== "")
      .join("\n");
  }
  if (rule.value == null) return "";
  return String(rule.value);
}

function parseVisibilityRuleValue(
  operator: VisibilityOperator,
  rawText: string,
): unknown {
  if (!visibilityOperatorNeedsValue(operator)) return null;

  if (operator === "in") {
    return rawText
      .split(/[\n,]/)
      .map((item) => item.trim())
      .filter(Boolean)
      .map((item) => coerceVisibilityScalar(item));
  }

  return coerceVisibilityScalar(rawText.trim());
}

function coerceVisibilityScalar(rawValue: string): string | number {
  if (/[T ]\d{2}:\d{2}/.test(rawValue)) {
    if (isIsoDateTimeString(rawValue)) return normalizeDateTimeSubmit(rawValue);
    const parsed = parseIsoDateTime(rawValue);
    if (parsed) {
      return `${toIsoDate(parsed)}T${pad2(parsed.getHours())}:${pad2(parsed.getMinutes())}`;
    }
  }

  const date = parseCalendarDate(rawValue);
  if (date) return toIsoDate(date);

  const numeric = Number(rawValue);
  if (
    rawValue !== "" &&
    Number.isFinite(numeric) &&
    /^-?\d+(\.\d+)?$/.test(rawValue)
  ) {
    return numeric % 1 === 0 ? Math.trunc(numeric) : numeric;
  }
  return rawValue;
}

function visibilityFromField(
  field: FieldDto,
): Pick<
  FormBuilderField,
  | "visibilityEnabled"
  | "visibilityField"
  | "visibilityOperator"
  | "visibilityValue"
  | "visibilityAction"
> {
  const config =
    field.visibility ??
    (isFieldOptionsObject(field.options)
      ? (field.options.visibility as VisibilityConfig | undefined)
      : undefined);

  if (!config?.rules?.length) {
    return {
      visibilityEnabled: false,
      visibilityField: "",
      visibilityOperator: "equals",
      visibilityValue: "",
      visibilityAction: "show",
    };
  }

  const rule = config.rules[0];
  const operator = (rule.operator ?? "equals") as VisibilityOperator;
  return {
    visibilityEnabled: true,
    visibilityField: rule.field ?? "",
    visibilityOperator: operator === "filled" ? "not_empty" : operator,
    visibilityValue: visibilityValueFromRule(rule),
    visibilityAction: (config.action === "hide"
      ? "hide"
      : "show") as VisibilityAction,
  };
}

export function fieldFromDto(field: FieldDto, index: number): FormBuilderField {
  const options = field.options;
  const filterable =
    field.select?.filterable != null
      ? parseBoolean(field.select.filterable)
      : isFieldOptionsObject(options)
        ? parseBoolean(options.filterable)
        : false;

  const withTime =
    field.dateRange?.with_time != null
      ? parseBoolean(field.dateRange.with_time)
      : field.date?.with_time != null
        ? parseBoolean(field.date.with_time)
        : isFieldOptionsObject(options)
          ? parseBoolean(options.with_time ?? options.withTime)
          : false;

  const minAge =
    field.birthdate != null
      ? readOptionalAge(field.birthdate.min_age)
      : isFieldOptionsObject(options)
        ? readOptionalAge(options.min_age ?? options.minAge)
        : null;

  const maxAge =
    field.birthdate != null
      ? readOptionalAge(field.birthdate.max_age)
      : isFieldOptionsObject(options)
        ? readOptionalAge(options.max_age ?? options.maxAge)
        : null;

  const patronymicRequired =
    field.fio?.patronymic_required != null
      ? parseBoolean(field.fio.patronymic_required)
      : isFieldOptionsObject(options)
        ? parseBoolean(options.patronymic_required)
        : false;

  const presentation = "stacked";
  const display = resolveDisplayConfig(field);
  const numberConfig = resolveNumberConfig(field);

  const loaded: FormBuilderField = {
    clientId: nextClientId("field"),
    id: normalizeSubmissionFieldId(field.id) ?? undefined,
    label: isConsentFieldType(field.type)
      ? defaultPrivacyFieldLabel()
      : readFieldLabel(field.label),
    description: readFieldDescription(field.description),
    name: field.name,
    type: field.type,
    required: isDisplayFieldType(field.type) ? false : field.required,
    order: field.order ?? index,
    choicesText: choicesFromField(field),
    quizCorrect: isFieldOptionsObject(options)
      ? parseQuizCorrect(options.correct)
      : [],
    quizPoints: isFieldOptionsObject(options)
      ? parseQuizPoints(options.points)
      : 1,
    filterable,
    withTime,
    numberMin: numberConfig.min,
    numberMax: numberConfig.max,
    numberStep: numberConfig.step,
    numberInteger: numberConfig.integer,
    minAge,
    maxAge,
    cityIds: cityIdsFromField(field),
    cityLabels: cityLabelsFromField(field),
    ...choicesByFromField(field),
    ...choicesUrlFromField(field),
    patronymicRequired,
    presentation,
    displayContent: display.content ?? "",
    displayImageUrl: display.image_url ?? "",
    displayImageNodeId: display.image_node_id ?? null,
    ...visibilityFromField(field),
    nameManuallyEdited: true,
    yandexType: field.yandexType ?? field.yandex_type ?? null,
  };
  loaded.choicesListMode = inferListChoicesMode(loaded);
  return loaded;
}

function sectionFromDto(
  section: SectionDto,
  index: number,
): FormBuilderSection {
  const fields = [...(section.fields ?? [])]
    .sort((a, b) => a.order - b.order)
    .map((field, fieldIndex) => fieldFromDto(field, fieldIndex));

  return {
    clientId: nextClientId("section"),
    id: section.id,
    label: section.label,
    order: section.order ?? index,
    fields,
  };
}

export function draftFromForm(form: FormDto): FormBuilderDraft {
  const sections = (form.sections ?? []).map((section, index) =>
    sectionFromDto(section, index),
  );

  if (sections.length === 0 && (form.fields?.length ?? 0) > 0) {
    sections.push({
      clientId: nextClientId("section"),
      label: "Основные данные",
      order: 0,
      fields: [...(form.fields ?? [])]
        .sort((a, b) => a.order - b.order)
        .map((field, index) => fieldFromDto(field, index)),
    });
  }

  if (sections.length === 0) {
    sections.push(createEmptySection(0));
  }

  return {
    name: form.name,
    description: form.description ?? "",
    isClosed: isFormClosed(form),
    closedMessage: isFormClosed(form) ? getFormClosedMessage(form, "") : "",
    submitHooksText: submitHooksTextFromForm(normalizeSubmitHooks(form)),
    sections,
    showSectionLabels: form.showSectionLabels !== false,
    sectionDisplayMode: form.sectionDisplayMode === "step" ? "step" : "all",
    showSubmissionAfterSubmit: isShowSubmissionAfterSubmit(form),
    interactionMode: resolveInteractionMode(form),
    quiz: resolveQuizConfig(form),
    appearanceTheme:
      form.appearance?.theme?.trim() || FALLBACK_FORM_THEMES.defaultTheme,
    appearanceOverrides: appearanceOverridesFromDto(form.appearance),
    appearanceCleared: false,
  };
}

function buildFieldOptions(
  field: FormBuilderField,
  includeQuiz = false,
): Record<string, unknown> | string[] | null {
  const type = field.type;
  const options: Record<string, unknown> = {};

  if (isListFieldType(type)) {
    const mode = listChoicesMode(field);
    if (mode === "url" && effectiveRemoteUrl(field.choicesUrl)) {
      options.choices_url = effectiveRemoteUrl(field.choicesUrl);
      if (field.choicesPath.trim())
        options.choices_path = field.choicesPath.trim();
      if (field.choicesItem.trim())
        options.choices_item = field.choicesItem.trim();
      if (field.choicesValue.trim())
        options.choices_value = field.choicesValue.trim();
    } else if (mode === "dependent" && field.choicesByField.trim()) {
      const source = field.choicesByField.trim();
      const byUrl = effectiveRemoteUrl(field.choicesByUrl);
      if (byUrl) {
        options.choices_by = {
          field: source,
          url: byUrl,
          ...(field.choicesByPath.trim()
            ? { path: field.choicesByPath.trim() }
            : {}),
          ...(field.choicesByItem.trim()
            ? { item: field.choicesByItem.trim() }
            : {}),
          ...(field.choicesByValue.trim()
            ? { value: field.choicesByValue.trim() }
            : {}),
        };
      } else {
        const map = buildChoicesByMap(field.choicesByMapText);
        if (Object.keys(map).length > 0) {
          options.choices_by = { field: source, map };
        }
      }
    } else {
      const choices = parseChoiceLines(field.choicesText);
      if (choices.length > 0) {
        options.choices = choices;
      }
    }
    if (type === "select" && field.filterable) {
      options.filterable = true;
    }
    if (includeQuiz) {
      const correct = serializeQuizCorrect(type, field.quizCorrect);
      if (correct != null) options.correct = correct;
      options.points = parseQuizPoints(field.quizPoints);
    }
  }

  if (type === "date" && field.withTime) {
    options.with_time = true;
  }
  if (type === "date-range") {
    options.with_time = Boolean(field.withTime);
  }

  if (type === "number") {
    Object.assign(
      options,
      serializeNumberOptions({
        min: field.numberMin,
        max: field.numberMax,
        step: field.numberStep,
        integer: field.numberInteger,
      }),
    );
  }

  if (type === "birthdate") {
    if (field.minAge != null) options.min_age = field.minAge;
    if (field.maxAge != null) options.max_age = field.maxAge;
  }

  if (type === "city") {
    options.city_ids = field.cityIds.map((id) => Math.trunc(id));
  }

  if (type === "fio" && field.patronymicRequired) {
    options.patronymic_required = true;
  }

  if (type === "display") {
    const content = field.displayContent.trim();
    const imageUrl = field.displayImageUrl.trim();
    if (content) options.content = content;
    if (imageUrl) options.image_url = imageUrl;
    if (imageUrl && field.displayImageNodeId != null) {
      options.image_node_id = field.displayImageNodeId;
    }
  }

  if (field.visibilityEnabled && field.visibilityField.trim()) {
    const operator = field.visibilityOperator || "equals";
    const rule: Record<string, unknown> = {
      field: field.visibilityField.trim(),
      operator,
    };
    if (visibilityOperatorNeedsValue(operator)) {
      rule.value = parseVisibilityRuleValue(operator, field.visibilityValue);
    }

    options.visibility = {
      action: field.visibilityAction === "hide" ? "hide" : "show",
      logic: "all",
      rules: [rule],
    };
  }

  if (Object.keys(options).length === 0) {
    return null;
  }

  return options;
}

export function builderFieldToFieldDto(
  field: FormBuilderField,
  formId = 0,
  includeQuiz = false,
): FieldDto {
  const options = buildFieldOptions(field, includeQuiz);
  const displayOptions =
    options && typeof options === "object" && !Array.isArray(options)
      ? options
      : null;

  return {
    id: field.id,
    form_id: formId,
    order: field.order,
    label: serializeFieldLabel(field.label),
    description: serializeFieldDescription(field.description),
    name: field.name,
    type: field.type,
    required: isDisplayFieldType(field.type) ? false : field.required,
    options,
    select:
      resolveFieldType(field.type) === "select" && displayOptions
        ? {
            filterable: Boolean(displayOptions.filterable),
            choices_by: displayOptions.choices_by
              ? (displayOptions.choices_by as NonNullable<
                  FieldDto["select"]
                >["choices_by"])
              : null,
          }
        : null,
    date:
      resolveFieldType(field.type) === "date"
        ? { with_time: Boolean(field.withTime) }
        : null,
    dateRange:
      resolveFieldType(field.type) === "date-range"
        ? { with_time: Boolean(field.withTime) }
        : null,
    number:
      resolveFieldType(field.type) === "number"
        ? {
            min: field.numberMin,
            max: field.numberMax,
            step: field.numberStep,
            integer: field.numberInteger,
          }
        : null,
    birthdate:
      resolveFieldType(field.type) === "birthdate"
        ? { min_age: field.minAge, max_age: field.maxAge }
        : null,
    city:
      resolveFieldType(field.type) === "city"
        ? {
            city_ids: field.cityIds,
            choices: field.cityIds.map((id) => ({
              value: id,
              label: field.cityLabels[String(id)] || String(id),
            })),
          }
        : null,
    display:
      resolveFieldType(field.type) === "display" && displayOptions
        ? {
            content: String(displayOptions.content ?? ""),
            image_url: String(displayOptions.image_url ?? ""),
            image_node_id:
              typeof displayOptions.image_node_id === "number"
                ? displayOptions.image_node_id
                : null,
          }
        : null,
  };
}

export function countDraftFields(draft: FormBuilderDraft): number {
  return draft.sections.reduce(
    (sum, section) => sum + section.fields.length,
    0,
  );
}

export function draftFieldNames(draft: FormBuilderDraft): Set<string> {
  const names = new Set<string>();
  for (const section of draft.sections) {
    for (const field of section.fields) {
      const name = field.name.trim();
      if (name) names.add(name);
    }
  }
  return names;
}

export function verifyDraftFieldsPreserved(
  before: FormBuilderDraft,
  after: FormBuilderDraft,
): string | null {
  const beforeCount = countDraftFields(before);
  const afterCount = countDraftFields(after);
  const beforeNames = draftFieldNames(before);
  const afterNames = draftFieldNames(after);
  const lost = [...beforeNames].filter((name) => !afterNames.has(name));

  if (beforeCount > 0 && afterCount === 0) {
    return "После сохранения все поля формы пропали. Локальная версия сохранена — не перезагружайте страницу без копии данных.";
  }

  if (lost.length > 0 || afterCount < beforeCount) {
    return `После сохранения пропали поля: ${lost.join(", ") || "часть полей"}. Локальная версия сохранена.`;
  }

  return null;
}

export type DraftSaveOptions = {
  fieldTypes?: ApiFieldTypeOption[];
  organisationId?: number | null;
  externalServiceId?: number | null;
};

export type DraftValidationOptions = DraftSaveOptions;

export function draftToSavePayload(
  draft: FormBuilderDraft,
  options?: DraftSaveOptions,
): FormSavePayload {
  let submitHooks: unknown[] | undefined;
  try {
    const parsed = JSON.parse(draft.submitHooksText || "[]");
    const hooks = Array.isArray(parsed)
      ? parsed
      : parsed && typeof parsed === "object"
        ? [parsed]
        : [];
    const normalized = normalizeSubmitHooksForSave(hooks);
    submitHooks = normalized.length > 0 ? normalized : undefined;
  } catch {
    submitHooks = undefined;
  }

  return {
    name: draft.name.trim(),
    description: draft.description.trim(),
    isClosed: draft.isClosed,
    closedMessage: draft.isClosed ? draft.closedMessage.trim() || null : null,
    ...(options?.organisationId !== undefined
      ? { organisationId: options.organisationId }
      : {}),
    ...(options?.externalServiceId !== undefined
      ? { externalServiceId: options.externalServiceId }
      : {}),
    ...(submitHooks ? { submitHooks } : {}),
    showSectionLabels: draft.showSectionLabels,
    sectionDisplayMode: draft.sectionDisplayMode,
    showSubmissionAfterSubmit: draft.showSubmissionAfterSubmit,
    interactionMode: draft.interactionMode,
    quiz:
      draft.interactionMode === "quiz"
        ? serializeQuizConfig(draft.quiz)
        : null,
    appearance: buildAppearanceSavePayload(
      draft.appearanceTheme,
      draft.appearanceOverrides,
      draft.appearanceCleared,
    ),
    sections: draft.sections.map((section, sectionIndex) => ({
      id: section.id,
      label: section.label.trim(),
      order: section.order ?? sectionIndex,
      fields: section.fields.map((field, fieldIndex) => ({
        id: field.id,
        label: isConsentFieldType(field.type)
          ? defaultPrivacyFieldLabel()
          : serializeFieldLabel(field.label),
        description: serializeFieldDescription(field.description),
        name: field.name.trim(),
        type: toApiFieldType(field.type),
        required: isDisplayFieldType(field.type) ? false : field.required,
        order: field.order ?? fieldIndex,
        options: buildFieldOptions(
          field,
          draft.interactionMode === "quiz",
        ),
      })),
    })),
  };
}

function fieldContextLabel(
  section: FormBuilderSection,
  sectionIndex: number,
  fieldIndex: number,
): string {
  return `Раздел «${section.label || sectionIndex + 1}», поле ${fieldIndex + 1}`;
}

export function validateDraft(
  draft: FormBuilderDraft,
  _options?: DraftValidationOptions,
): string[] {
  const errors: string[] = [];

  if (!draft.name.trim()) {
    errors.push("Укажите название формы");
  }

  if (draft.sections.length === 0) {
    errors.push("Добавьте хотя бы один раздел");
  }

  try {
    const parsed = JSON.parse(draft.submitHooksText || "[]");
    if (!Array.isArray(parsed) && !(parsed && typeof parsed === "object")) {
      errors.push("Submit hooks: ожидается массив или объект JSON");
    }
  } catch {
    errors.push("Submit hooks: некорректный JSON");
  }

  const names = new Set<string>();
  const allFieldNames = new Set<string>();

  draft.sections.forEach((section) => {
    section.fields.forEach((field) => {
      const name = field.name.trim();
      if (name) allFieldNames.add(name);
    });
  });

  draft.sections.forEach((section, sectionIndex) => {
    if (!section.label.trim()) {
      errors.push(`Раздел ${sectionIndex + 1}: укажите название`);
    }

    section.fields.forEach((field, fieldIndex) => {
      const label = fieldContextLabel(section, sectionIndex, fieldIndex);

      if (!field.name.trim()) {
        errors.push(`${label}: укажите имя (name)`);
      } else if (names.has(field.name.trim())) {
        errors.push(`${label}: имя «${field.name}» уже используется`);
      } else {
        names.add(field.name.trim());
      }

      if (isConsentFieldType(field.type)) {
        // label необязателен
      } else if (!isKnownFieldType(field.type)) {
        errors.push(`${label}: неизвестный тип «${field.type}»`);
      }

      if (isListFieldType(field.type)) {
        const mode = listChoicesMode(field);
        if (mode === "dependent") {
          const source = field.choicesByField.trim();
          if (!source) {
            errors.push(`${label}: укажите поле-источник зависимого списка`);
          } else if (source === field.name.trim()) {
            errors.push(
              `${label}: зависимый список не может ссылаться на себя`,
            );
          } else if (!allFieldNames.has(source)) {
            errors.push(`${label}: поле-источник «${source}» не найдено`);
          }
          if (effectiveRemoteUrl(field.choicesByUrl)) {
            // url instead of map
          } else if (field.choicesByUrl.trim() === "https://") {
            errors.push(`${label}: укажите ссылку на список`);
          } else {
            const map = buildChoicesByMap(field.choicesByMapText);
            if (Object.keys(map).length === 0) {
              errors.push(`${label}: добавьте варианты для зависимого списка`);
            }
          }
        } else if (mode === "url") {
          if (!effectiveRemoteUrl(field.choicesUrl)) {
            errors.push(`${label}: укажите ссылку на список`);
          }
        } else if (!field.choicesText.trim()) {
          errors.push(`${label}: добавьте варианты ответа`);
        }
      }

      if (isDisplayFieldType(field.type)) {
        if (!field.displayContent.trim() && !field.displayImageUrl.trim()) {
          errors.push(`${label}: укажите текст или картинку`);
        }
      }

      if (
        field.type === "birthdate" &&
        field.minAge != null &&
        field.maxAge != null &&
        field.minAge > field.maxAge
      ) {
        errors.push(
          `${label}: минимальный возраст не может быть больше максимального`,
        );
      }

      if (
        field.type === "number" &&
        field.numberMin != null &&
        field.numberMax != null &&
        field.numberMin > field.numberMax
      ) {
        errors.push(`${label}: минимум не может быть больше максимума`);
      }
      if (field.type === "number" && field.numberStep != null && field.numberStep <= 0) {
        errors.push(`${label}: шаг должен быть больше 0`);
      }

      if (field.visibilityEnabled && !field.visibilityField.trim()) {
        errors.push(`${label}: укажите поле для условия видимости`);
      }
      if (
        field.visibilityEnabled &&
        visibilityOperatorNeedsValue(field.visibilityOperator || "equals")
      ) {
        if (!field.visibilityValue.trim()) {
          errors.push(`${label}: укажите значение для условия видимости`);
        } else {
          const source = draft.sections
            .flatMap((section) => section.fields)
            .find((item) => item.name === field.visibilityField.trim());
          if (source && isDateLikeFieldType(source.type)) {
            const withTime = source.type === "date" && source.withTime;
            if (!isValidDateSubmit(field.visibilityValue, withTime)) {
              errors.push(
                withTime
                  ? `${label}: в условии видимости укажите дату и время`
                  : `${label}: в условии видимости укажите дату`,
              );
            }
          }
        }
      }
    });
  });

  return errors;
}

function mapDraftFieldToSave(field: FormBuilderField, fieldIndex: number) {
  return {
    id: field.id,
    label: isConsentFieldType(field.type)
      ? defaultPrivacyFieldLabel()
      : serializeFieldLabel(field.label),
    description: serializeFieldDescription(field.description),
    name: field.name.trim(),
    type: toApiFieldType(field.type),
    required: isDisplayFieldType(field.type) ? false : field.required,
    order: field.order ?? fieldIndex,
    options: buildFieldOptions(field),
  };
}

export function draftToYandexSavePayload(
  draft: FormBuilderDraft,
  organisationId?: number | null,
): FormSavePayload {
  const fields = draft.sections.flatMap((section) =>
    section.fields.map((field, fieldIndex) =>
      mapDraftFieldToSave(field, fieldIndex),
    ),
  );

  return {
    name: draft.name.trim(),
    description: draft.description.trim(),
    isClosed: draft.isClosed,
    closedMessage: draft.isClosed ? draft.closedMessage.trim() || null : null,
    ...(organisationId !== undefined ? { organisationId } : {}),
    fields,
  };
}

export function validateYandexDraft(draft: FormBuilderDraft): string[] {
  const errors: string[] = [];

  if (!draft.name.trim()) {
    errors.push("Укажите название формы");
  }

  const names = new Set<string>();
  draft.sections.forEach((section, sectionIndex) => {
    section.fields.forEach((field, fieldIndex) => {
      const contextLabel = fieldContextLabel(section, sectionIndex, fieldIndex);
      const name = field.name.trim();
      if (!name) {
        errors.push(`${contextLabel}: укажите имя (name)`);
      } else if (names.has(name)) {
        errors.push(`${contextLabel}: имя «${name}» уже используется`);
      } else {
        names.add(name);
      }
    });
  });

  return errors;
}
